import { DependencyList, useEffect } from "react";
import { AxiosRequestConfig, AxiosResponse } from "axios";
import axios from "../services/axios";
import { useReportLoading } from "../contexts/ReportLoadingContext";

export type ReportRequestClient = Pick<typeof axios, "get">;

/** Each effect owns its requests, cancellation and errors, including dependent requests. */
export function useReportEffect(effect: (client: ReportRequestClient) => void | (() => void), dependencies: DependencyList) {
  const { update, revision } = useReportLoading();
  useEffect(() => {
    const id = Symbol("report-request");
    const controller = new AbortController();
    let pending = 0;
    let failed = false;
    const publish = () => { if (!controller.signal.aborted) update(id, { pending, failed }); };
    const client: ReportRequestClient = {
      get: async <T = any, R = AxiosResponse<T>, D = any>(url: string, config?: AxiosRequestConfig<D>): Promise<R> => {
        const requestController = new AbortController();
        const abort = () => requestController.abort();
        controller.signal.addEventListener("abort", abort);
        config?.signal?.addEventListener("abort", abort);
        if (controller.signal.aborted || config?.signal?.aborted) abort();
        pending++;
        publish();
        try {
          const response = await axios.get<T, R, D>(url, { ...config, signal: requestController.signal });
          if (requestController.signal.aborted) throw Object.assign(new Error("Cancelled report request"), { code: "ERR_CANCELED", name: "CanceledError" });
          return response;
        } catch (error) {
          if (!requestController.signal.aborted) failed = true;
          throw error;
        } finally {
          pending--;
          controller.signal.removeEventListener("abort", abort);
          config?.signal?.removeEventListener("abort", abort);
          publish();
        }
      },
    };
    const cleanup = effect(client);
    return () => {
      controller.abort();
      cleanup?.();
      update(id);
    };
    // The caller supplies the dependencies of its query, just like useEffect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...dependencies, revision, update]);
}
